import { FC, useMemo, useRef } from 'react';

import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { PRODUCT_SCORE_CRITERIA } from 'pages/ListManagement/const';
import { MObjectifScoreHeader, translateData, translateHeaders } from './helpers/HeadersList';
import { listValidator, numberValidator } from '../Validators';
import Grid from '../Grid';
import { listAllowedEmpty, requireTitle } from './helpers/utils';
import { AutoCompleteRenderer } from './helpers/customRenderers';

export const MOjectifScore: FC<{
    list: IList;
    toolId: number;
    retailers: string[];
    categories: string[];
}> = ({ list, toolId, retailers, categories }) => {
    const hotRef = useRef<HotTable | null>(null);
    const translatedHeaders = translateHeaders(MObjectifScoreHeader);
    const translatedCriteria = translateData(PRODUCT_SCORE_CRITERIA);
    const translatedYesNo = translateData(['Yes', 'No']);
    const categoriesWithEmpty = listAllowedEmpty(categories);
    const mapCriteria: any = {};
    const mapIDToCriteria: any = {};

    translatedCriteria.forEach((tC, i) => {
        mapCriteria[tC] = PRODUCT_SCORE_CRITERIA[i].split('.')[2];
        mapIDToCriteria[PRODUCT_SCORE_CRITERIA[i].split('.')[2]] = tC;
    });

    translatedYesNo.forEach((tC, i) => {
        mapCriteria[tC] = ['Yes', 'No'][i];
        mapIDToCriteria[['Yes', 'No'][i]] = tC;
    });

    const afterValidate = (listToValidate: IList) => {
        const newData = [...listToValidate.data].map((row) => {
            const newRow = [...row];
            if (row[2]) {
                newRow[2] = mapCriteria[newRow[2] as string];
            }

            if (row[4]) {
                newRow[4] = mapCriteria[newRow[4] as string];
            }
            return newRow;
        });

        return { ...listToValidate, data: newData };
    };

    const formatList = (l: IList) => {
        const newData = l.data.map((data) => {
            const newRow = [...data];
            newRow[2] = mapIDToCriteria[newRow[2] as string];
            newRow[4] = mapIDToCriteria[newRow[4] as string];
            return newRow;
        });

        return {
            ...l,
            data: newData,
        };
    };

    const formatedList = useMemo(() => formatList(list), [list.data, list.name]);

    return (
        <Grid
            toolId={toolId}
            headers={MObjectifScoreHeader}
            list={formatedList}
            paramHotRef={hotRef}
            afterValidate={afterValidate}
        >
            <HotColumn
                validator={listValidator(retailers)}
                title={requireTitle(translatedHeaders[0])}
                source={retailers}
                type="autocomplete"
            >
                <AutoCompleteRenderer list={retailers} hot-renderer />
            </HotColumn>
            <HotColumn
                validator={listValidator(categoriesWithEmpty)}
                title={requireTitle(translatedHeaders[1])}
                source={categoriesWithEmpty}
                type="autocomplete"
                allowEmpty={true}
            >
                <AutoCompleteRenderer list={categoriesWithEmpty} hot-renderer />
            </HotColumn>
            <HotColumn
                validator={listValidator(translatedCriteria)}
                title={requireTitle(translatedHeaders[2])}
                source={translatedCriteria}
                type="autocomplete"
            >
                <AutoCompleteRenderer list={translatedCriteria} hot-renderer />
            </HotColumn>
            <HotColumn title={requireTitle(translatedHeaders[3])} type="numeric" validator={numberValidator()} />
            <HotColumn
                title={requireTitle(translatedHeaders[4])}
                validator={listValidator(translatedYesNo)}
                type="autocomplete"
                source={translatedYesNo}
            >
                <AutoCompleteRenderer list={translatedYesNo} hot-renderer />
            </HotColumn>
        </Grid>
    );
};
